import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DesktopLayout from '../layouts/DesktopLayout';
import serviceApi from '../services/serviceApi';
import { formatDate } from '../utils/dateFormatter';
import { formatSAR } from '../utils/currencyFormatter';
import InvoiceStatusBadge from '../components/invoices/InvoiceStatusBadge';
import '../styles/ServiceClients.css';
import '../styles/Invoices.css';

const STATUS_OPTIONS = [
  { value: '',          label: 'كل الحالات' },
  { value: 'issued',    label: 'صادرة' },
  { value: 'paid',      label: 'مدفوعة' },
  { value: 'cancelled', label: 'ملغاة' }
];

const PAGE_SIZE = 25;

const InvoicesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('services');

  const status     = searchParams.get('status')     || '';
  const from       = searchParams.get('from')       || '';
  const to         = searchParams.get('to')         || '';
  const q          = searchParams.get('q')          || '';
  const clientName = searchParams.get('clientName') || '';
  const clientId   = searchParams.get('clientId')   || '';
  const page       = parseInt(searchParams.get('page') || '1', 10);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value === null || value === undefined) next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = { page, pageSize: PAGE_SIZE };
      if (status)     filters.status     = status;
      if (from)       filters.from       = from;
      if (to)         filters.to         = to;
      if (q)          filters.q          = q;
      if (clientName) filters.clientName = clientName;
      if (clientId)   filters.clientId   = clientId;
      const res = await serviceApi.invoices.list(filters);
      const data = res.data || {};
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('تعذر تحميل الفواتير');
    } finally {
      setLoading(false);
    }
  }, [status, from, to, q, clientName, clientId, page]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleCsvExport = async () => {
    try {
      setCsvLoading(true);
      const filters = {};
      if (status)     filters.status     = status;
      if (from)       filters.from       = from;
      if (to)         filters.to         = to;
      if (q)          filters.q          = q;
      if (clientName) filters.clientName = clientName;
      if (clientId)   filters.clientId   = clientId;
      const csv = await serviceApi.invoices.exportCsv(filters);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('تعذر تصدير الفواتير');
    } finally {
      setCsvLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const sourceLabel = (inv) => {
    if (inv.subscriptionId) return { text: 'اشتراك', cls: 'inv-source--sub', icon: 'bi-arrow-repeat' };
    return { text: 'زيارة فردية', cls: 'inv-source--per-visit', icon: 'bi-calendar-check' };
  };

  return (
    <DesktopLayout
      activeNavItem={activeNavItem}
      setActiveNavItem={setActiveNavItem}
      headerTitle="الفواتير"
    >
      <div className="svc-clients" dir="rtl">

        {/* Header */}
        <div className="svc-clients__header">
          <h3 className="svc-clients__title">الفواتير</h3>
          <button
            className="inv-csv-btn"
            onClick={handleCsvExport}
            disabled={csvLoading || items.length === 0}
            title="تصدير CSV"
          >
            <i className="bi bi-download" />
            {csvLoading ? 'جارٍ التصدير…' : 'تصدير CSV'}
          </button>
        </div>

        {/* Filters */}
        <div className="inv-filters">
          <div className="inv-filter-group">
            <label htmlFor="inv-filter-status">الحالة</label>
            <select
              id="inv-filter-status"
              value={status}
              onChange={(e) => updateParam('status', e.target.value)}
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="inv-filter-group">
            <label htmlFor="inv-filter-from">من تاريخ</label>
            <input
              id="inv-filter-from"
              type="date"
              value={from}
              onChange={(e) => updateParam('from', e.target.value)}
            />
          </div>
          <div className="inv-filter-group">
            <label htmlFor="inv-filter-to">إلى تاريخ</label>
            <input
              id="inv-filter-to"
              type="date"
              value={to}
              onChange={(e) => updateParam('to', e.target.value)}
            />
          </div>
          <div className="inv-filter-group" style={{ flex: 1, minWidth: 180 }}>
            <label htmlFor="inv-filter-q">رقم الفاتورة</label>
            <input
              id="inv-filter-q"
              type="text"
              placeholder="INV-2026-…"
              value={q}
              onChange={(e) => updateParam('q', e.target.value)}
            />
          </div>
          <div className="inv-filter-group" style={{ flex: 1, minWidth: 180 }}>
            <label htmlFor="inv-filter-client">اسم العميل</label>
            <input
              id="inv-filter-client"
              type="text"
              placeholder="بحث بالاسم..."
              value={clientName}
              onChange={(e) => updateParam('clientName', e.target.value)}
            />
          </div>
        </div>

        {/* Active client-id filter banner (when arriving from a deep-link) */}
        {clientId && !clientName && (
          <div style={{
            background: '#f4f0ff',
            border: '1px solid #d3c4e8',
            color: '#471396',
            padding: '8px 12px',
            borderRadius: 6,
            fontSize: '0.85rem',
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              <i className="bi bi-funnel-fill" style={{ marginInlineStart: 6 }} />
              مرشّح حسب عميل محدد
            </span>
            <button
              onClick={() => updateParam('clientId', '')}
              style={{ background: 'none', border: 'none', color: '#471396', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              <i className="bi bi-x-circle" /> إزالة
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="svc-clients__empty">
            <div className="svc-clients__empty-icon"><i className="bi bi-exclamation-triangle" /></div>
            <p className="svc-clients__empty-text">{error}</p>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && items.length === 0 && (
          <div className="svc-clients__empty">
            <div className="svc-clients__empty-icon"><i className="bi bi-receipt" /></div>
            <p className="svc-clients__empty-text">لا توجد فواتير مطابقة</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && items.length > 0 && (
          <>
            <div className="svc-clients__table-wrap">
              <table className="svc-clients__table">
                <thead>
                  <tr>
                    <th>رقم الفاتورة</th>
                    <th>العميل</th>
                    <th>المصدر</th>
                    <th>الباقة</th>
                    <th>المبلغ الإجمالي</th>
                    <th>تاريخ الإصدار</th>
                    <th>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((inv) => {
                    const src = sourceLabel(inv);
                    const clientName = inv.clientId?.userId?.name || '—';
                    const clientCode = inv.clientId?.userId?.clientCode;
                    const invTotal = inv.commercials?.totalAmount;
                    const refundsCount = (inv.refunds || []).length;
                    return (
                      <tr
                        key={inv._id}
                        onClick={() => navigate(`/services/invoices/${inv._id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td><span className="inv-number">{inv.invoiceNumber}</span></td>
                        <td>
                          <div className="svc-client-name">{clientName}</div>
                          {clientCode && <div className="svc-client-code">{clientCode}</div>}
                        </td>
                        <td>
                          <span className={`inv-source ${src.cls}`}>
                            <i className={`bi ${src.icon}`} />
                            {src.text}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.85rem', color: '#495057' }}>
                          {inv.packageSnapshot?.packageName || '—'}
                        </td>
                        <td style={{ fontWeight: 600 }}>{formatSAR(invTotal)}</td>
                        <td style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                          {inv.issueDate ? formatDate(inv.issueDate) : '—'}
                        </td>
                        <td>
                          <InvoiceStatusBadge status={inv.status} refundsCount={refundsCount} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="inv-pagination">
              <button
                onClick={() => updateParam('page', String(Math.max(1, page - 1)))}
                disabled={page <= 1}
              >
                <i className="bi bi-chevron-right" /> السابق
              </button>
              <span>صفحة {page} من {totalPages} — إجمالي {total}</span>
              <button
                onClick={() => updateParam('page', String(Math.min(totalPages, page + 1)))}
                disabled={page >= totalPages}
              >
                التالي <i className="bi bi-chevron-left" />
              </button>
            </div>
          </>
        )}

      </div>
    </DesktopLayout>
  );
};

export default InvoicesPage;
